import { Controller } from "@hotwired/stimulus"

// Contextual Help Tooltip Controller
// Provides interactive tooltips and help popovers for complex UI elements
//
// Usage:
//   <div data-controller="help-tooltip">
//     <button data-action="click->help-tooltip#toggle"
//             data-help-tooltip-content-value="Help text here">
//       <svg>...</svg> <!-- Help icon -->
//     </button>
//   </div>
export default class extends Controller {
  static values = {
    content: String,
    position: { type: String, default: "top" }, // top, bottom, left, right
    width: { type: String, default: "300px" }
  }

  static targets = ["trigger", "popover"]

  connect() {
    this.popoverElement = null
    this.isOpen = false

    // Close on escape key
    this.escapeHandler = this.handleEscape.bind(this)
    document.addEventListener("keydown", this.escapeHandler)

    // Close when clicking outside
    this.clickOutsideHandler = this.handleClickOutside.bind(this)
    document.addEventListener("click", this.clickOutsideHandler)
  }

  disconnect() {
    document.removeEventListener("keydown", this.escapeHandler)
    document.removeEventListener("click", this.clickOutsideHandler)
    this.closePopover()
  }

  toggle(event) {
    event.preventDefault()
    event.stopPropagation()

    if (this.isOpen) {
      this.closePopover()
    } else {
      this.openPopover(event.currentTarget)
    }
  }

  openPopover(triggerElement) {
    // Close any other open popovers
    document.querySelectorAll('.help-popover').forEach(el => el.remove())

    // Create popover element
    this.popoverElement = document.createElement('div')
    this.popoverElement.className = 'help-popover'
    this.popoverElement.innerHTML = `
      <div class="help-popover-content">
        ${this.contentValue}
      </div>
      <div class="help-popover-arrow"></div>
    `

    // Add to document
    document.body.appendChild(this.popoverElement)

    // Position the popover
    this.positionPopover(triggerElement)

    // Mark as open
    this.isOpen = true

    // Animate in
    requestAnimationFrame(() => {
      this.popoverElement.classList.add('show')
    })
  }

  closePopover() {
    if (this.popoverElement) {
      this.popoverElement.classList.remove('show')

      setTimeout(() => {
        if (this.popoverElement && this.popoverElement.parentNode) {
          this.popoverElement.remove()
        }
        this.popoverElement = null
      }, 200) // Match CSS transition duration
    }

    this.isOpen = false
  }

  positionPopover(triggerElement) {
    if (!this.popoverElement) return

    const triggerRect = triggerElement.getBoundingClientRect()
    const popoverRect = this.popoverElement.getBoundingClientRect()
    const viewportWidth = window.innerWidth
    const viewportHeight = window.innerHeight
    const scrollY = window.scrollY
    const scrollX = window.scrollX

    let top, left
    let arrowPosition = this.positionValue

    // Calculate position based on preferred position
    switch (this.positionValue) {
      case 'top':
        top = triggerRect.top + scrollY - popoverRect.height - 12
        left = triggerRect.left + scrollX + (triggerRect.width / 2) - (popoverRect.width / 2)

        // Check if overflowing viewport
        if (top < scrollY) {
          // Flip to bottom
          top = triggerRect.bottom + scrollY + 12
          arrowPosition = 'bottom'
        }
        break

      case 'bottom':
        top = triggerRect.bottom + scrollY + 12
        left = triggerRect.left + scrollX + (triggerRect.width / 2) - (popoverRect.width / 2)

        // Check if overflowing viewport
        if (top + popoverRect.height > scrollY + viewportHeight) {
          // Flip to top
          top = triggerRect.top + scrollY - popoverRect.height - 12
          arrowPosition = 'top'
        }
        break

      case 'left':
        top = triggerRect.top + scrollY + (triggerRect.height / 2) - (popoverRect.height / 2)
        left = triggerRect.left + scrollX - popoverRect.width - 12

        // Check if overflowing viewport
        if (left < scrollX) {
          // Flip to right
          left = triggerRect.right + scrollX + 12
          arrowPosition = 'right'
        }
        break

      case 'right':
        top = triggerRect.top + scrollY + (triggerRect.height / 2) - (popoverRect.height / 2)
        left = triggerRect.right + scrollX + 12

        // Check if overflowing viewport
        if (left + popoverRect.width > scrollX + viewportWidth) {
          // Flip to left
          left = triggerRect.left + scrollX - popoverRect.width - 12
          arrowPosition = 'left'
        }
        break
    }

    // Prevent horizontal overflow
    if (left < scrollX + 10) {
      left = scrollX + 10
    } else if (left + popoverRect.width > scrollX + viewportWidth - 10) {
      left = scrollX + viewportWidth - popoverRect.width - 10
    }

    // Apply position
    this.popoverElement.style.top = `${top}px`
    this.popoverElement.style.left = `${left}px`
    this.popoverElement.style.maxWidth = this.widthValue

    // Position arrow
    this.popoverElement.setAttribute('data-position', arrowPosition)
  }

  handleEscape(event) {
    if (event.key === "Escape" && this.isOpen) {
      this.closePopover()
    }
  }

  handleClickOutside(event) {
    if (this.isOpen &&
        this.popoverElement &&
        !this.popoverElement.contains(event.target) &&
        !this.element.contains(event.target)) {
      this.closePopover()
    }
  }
}
